import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '../auth';
import { bootstrapWireClient } from '../bootstrap';
import { ContextMenu } from '../context-menu';
import { useToasts } from '../toasts';
import { useLiveWire } from '../wire';
import { useWorlds } from '../worlds';
import { ChannelList } from './ChannelList';
import { ConnectionStatus } from './ConnectionStatus';
import { DmSidebar } from './DmSidebar';
import { MainPane } from './MainPane';
import { ServerRail } from './ServerRail';
import styles from './shell.module.css';

export function Shell(): JSX.Element {
  const { claimId } = useParams<{ claimId: string }>();
  const loadWorlds = useWorlds((s) => s.loadWorlds);
  const getWorldForClaim = useWorlds((s) => s.getWorldForClaim);
  const teardownRef = useRef<(() => void) | null>(null);
  const clientRef = useRef<{ close(): void } | null>(null);
  const eventUnsubRef = useRef<(() => void) | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    function clearRefreshTimer(): void {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }

    function scheduleProactiveRefresh(): void {
      clearRefreshTimer();
      const token = useAuth.getState().accessToken;
      if (!token) return;
      try {
        const parts = token.split('.');
        if (parts.length !== 3) return;
        const payload = JSON.parse(
          atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')),
        ) as Record<string, unknown>;
        const exp = typeof payload['exp'] === 'number' ? (payload['exp'] as number) : 0;
        const refreshAt = (exp - 120) * 1000 - Date.now();
        if (refreshAt <= 0) return; // already in the danger window; D-4 handles it
        refreshTimerRef.current = setTimeout(() => {
          refreshTimerRef.current = null;
          void useAuth.getState().refresh();
        }, refreshAt);
      } catch {
        // Unparseable token — leave the timer unset; D-4 will catch it on next connect.
      }
    }

    async function doBootstrap(): Promise<void> {
      await loadWorlds();
      if (cancelled) return;

      const world = getWorldForClaim(Number(claimId));
      if (!world) {
        useToasts.getState().push('error', 'World not found for this claim');
        return;
      }

      const teardown = await bootstrapWireClient(world.world_id);
      if (cancelled) {
        teardown();
        return;
      }
      teardownRef.current = teardown;
      scheduleProactiveRefresh();

      // Subscribe to auth_expired events so we can recover without a page reload.
      const client = useLiveWire.getState().client;
      if (!client) return;

      const unsubEvent = client.onEvent((ev) => {
        if (ev.kind !== 'auth_expired') return;

        void (async () => {
          const ok = await useAuth.getState().refresh();
          if (cancelled) return;

          if (ok) {
            // Tear down the stale client and re-bootstrap with the fresh token.
            if (eventUnsubRef.current) {
              eventUnsubRef.current();
              eventUnsubRef.current = null;
            }
            clearRefreshTimer();
            if (teardownRef.current) {
              teardownRef.current();
              teardownRef.current = null;
            }
            await doBootstrap();
          } else {
            // Refresh failed — log out; router will redirect to login.
            useAuth.getState().logout();
          }
        })();
      });

      eventUnsubRef.current = unsubEvent;
    }

    void doBootstrap();

    clientRef.current = useLiveWire.getState().client;

    return () => {
      cancelled = true;
      clearRefreshTimer();
      if (eventUnsubRef.current) {
        eventUnsubRef.current();
        eventUnsubRef.current = null;
      }
      if (teardownRef.current) {
        teardownRef.current();
        teardownRef.current = null;
      } else if (clientRef.current) {
        clientRef.current.close();
        useLiveWire.getState().setClient(null);
      }
      clientRef.current = null;
    };
  }, [claimId, loadWorlds, getWorldForClaim]);

  return (
    <div className={styles.shell} onContextMenu={(e) => e.preventDefault()}>
      <ConnectionStatus />
      <ServerRail />
      <ChannelList />
      <MainPane />
      <DmSidebar />
      <ContextMenu />
    </div>
  );
}
